import { makeAutoObservable } from 'mobx'
import RootStore  from 'mobx-stores/root-store'
import { Department, DepartmentCategory, DepartmentSubcategory, DepartmentTypes } from 'models/departments'
import { ProductsDTO } from 'models/products'
import { Api } from 'services'

export class ProductDetailsStore {
  private rootStore:any
  private departmentsApi: Api['departmentsApi']

  constructor (rootStore:any) {
    this.rootStore = rootStore;
    this.departmentsApi = this.rootStore.generalStore.api.departmentsApi;
    makeAutoObservable(this);
  }

  // OBSERVABLES
  private productDetails: Partial<ProductsDTO> = null;
  private departments: Department[] = [];
  private categories: DepartmentCategory[] = [];
  private subcategories: DepartmentSubcategory[] = [];
  private types: DepartmentTypes[] = [];

  // ACTIONS
  public setProductDetailsStore = (productDetails: Partial<ProductsDTO>): void => {
    this.productDetails = productDetails;
  }

  public setProductDetailsStoreProps = (props: Partial<ProductsDTO>): void => {
    this.setProductDetailsStore({ ...this.productDetails, ...props })
  }

  public updateProductDetails = (productId: number | string) => {
    this.rootStore.viewProductsPageStore.updateProduct(this.productDetails, productId);
  }

  public InitProductDetails = (): void => {
    this.productDetails = null;
    this.departments = [];
    this.categories = [];
    this.subcategories = [];
    this.types = [];
  }

  public resetDepartments = (): void => {
    this.setProductDetailsStoreProps({category: null})
    this.setProductDetailsStoreProps({subcategory: null})
    this.categories = [];
    this.subcategories = [];
    this.types = [];
  }

  public resetCategories = (): void => {
    this.setProductDetailsStoreProps({subcategory: null});
    this.subcategories = [];
    this.types = [];
  }

  public resetSubcategories = (): void => {
    this.setProductDetailsStoreProps({type: null});
    this.types = [];
  }


  // COMPUTED
  public get productDetailsStore(): Partial<ProductsDTO> {
    return this.productDetails
  }

  public get allDepartments (): Department[] {
    return this.departments;
  }

  public get allCategories (): DepartmentCategory[] {
    return this.categories;
  }

  public get allSubcategories (): DepartmentSubcategory[] {
    return this.subcategories;
  }

  public get allTypes (): DepartmentTypes[] {
    return this.types;
  }

  public get selectedDepartment(): Department[] {
    return this.departments.filter((department: Department) => department?.id === this.productDetails?.department?.id)
  }

  public get selectedCategory(): DepartmentCategory[] {
    return this.categories.filter((category: DepartmentCategory) => category?.id === this.productDetails?.category?.id)
  }

  public get selectedSubcategory(): DepartmentSubcategory[] {
    return this.subcategories.filter((subcategory: DepartmentSubcategory) => subcategory?.id === this.productDetails?.subcategory?.id)
  }

  public get selectedType(): DepartmentTypes[] {
    return this.types.filter((type: DepartmentTypes) => type?.id === this.productDetails?.type?.id)
  }

  //METHODS
  public getAllDepartments = async (): Promise<Department[]> => {
    const allDepartments = await this.departmentsApi.getAllDepartments();
    if (!allDepartments) return;
    this.departments = allDepartments;
    if(this.productDetails?.department?.id) {
      this.getAllCategories();
    }
  }

  public getAllCategories = async (): Promise<DepartmentCategory[]> => {
    const departmentId = this.productDetails?.department?.id;
    if(!departmentId) return;
    const allCategories = await this.departmentsApi.getDepartmentCategories(departmentId);
    if (!allCategories) return;
    this.categories = allCategories;
    if(this.productDetails?.category?.id) {
      this.getSubcategories();
      return;
    } 
    if(this.categories.length === 1) {
      this.setProductDetailsStoreProps({category: {id: this.categories[0]?.id}})
      this.getSubcategories();
    }
  }

  public getSubcategories = async (): Promise<DepartmentSubcategory[]> => {
    const categoryId = this.productDetails?.category?.id;
    if(!categoryId) return;
    const allSubcategories = await this.departmentsApi.getDepartmentSubcategories(categoryId);
    if (!allSubcategories) return;
    this.subcategories = allSubcategories;
    if(this.productDetails?.subcategory?.id) {
      this.getTypes();
      return;
    }
    if(this.subcategories.length === 1) {
      this.setProductDetailsStoreProps({subcategory: {id: this.subcategories[0]?.id}})
      this.getTypes();
    }
  }

  public getTypes = async (): Promise<DepartmentTypes[]> => {
    const subCategoryId = this.productDetails?.subcategory?.id;
    if(!subCategoryId) return;
    const allTypes = await this.departmentsApi.getDepartmentTypes(subCategoryId);
    if (!allTypes) return;
    this.types = allTypes;
  }

}